import React, { Suspense, lazy, useRef, useState } from "react";
import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { signInAnonymously, signInWithEmailAndPassword, signOut } from "firebase/auth";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import SimpleAlbumsSection from "./components/SimpleAlbumsSection";
import VideoStoriesSection from "./components/VideoStoriesSection";
import RealMosaicGallery from "./components/RealMosaicGallery";
import AboutSection from "./components/AboutSection";
import BlogSection from "./components/BlogSection";
import BlogPostPage from "./components/BlogPostPage";
import TestimonialsSection from "./components/TestimonialsSection";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import AlbumModal from "./components/AlbumModal";
import LightboxModal from "./components/LightboxModal";
import BookingModal from "./components/BookingModal";
import WebsiteAssistant from "./components/WebsiteAssistant";
import { Toaster } from "./components/ui/toaster";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Button } from "./components/ui/button";
import { heroSliderImages, testimonials } from "./data/mockData";
import { getBlogPostBySlug } from "./data/blogPosts";
import { auth } from "./lib/firebase";
import { getAlbums, getHomepageSettings, getImages, getVideos, resolveImageUrl } from "./lib/firebaseData";
import {
  getDeletedAlbumIds,
  getDeletedAlbumTitles,
  getDeletedVideoIds,
  getDeletedVideoTitles,
  getLocalAlbums,
  getLocalHomepageSettings,
  getLocalImages,
  getLocalTestimonials,
  getLocalVideos,
  setLocalHomepageSettings,
  seedLocalDefaultsIfEmpty
} from "./lib/localContentStore";

const ADMIN_SESSION_KEY = "admin_auth_ok";
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "admin123";
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "";
const SITE_URL = process.env.REACT_APP_SITE_URL || "";
const AdminPanelLazy = lazy(() => import("./components/EnhancedAdminPanel"));

const isRenderableImageUrl = (value) =>
  typeof value === "string" &&
  (value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:") ||
    value.startsWith("blob:"));

const DeferredSection = ({ minHeight = "24rem", children }) => {
  const containerRef = useRef(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} style={{ minHeight }}>
      {shouldRender ? children : null}
    </div>
  );
};

const getFirebaseAuthErrorMessage = (error) => {
  switch (error?.code) {
    case "auth/operation-not-allowed":
      return "Enable Anonymous sign-in in Firebase Authentication.";
    case "auth/unauthorized-domain":
      return "Add your Hostinger domain to Firebase Authentication > Settings > Authorized domains.";
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
      return "Firebase sign-in failed. Check the Firebase admin email/password.";
    default:
      return error?.message || "Firebase authentication failed.";
  }
};

const Home = () => {
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [isAlbumModalOpen, setIsAlbumModalOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [dynamicAlbums, setDynamicAlbums] = useState([]);
  const [dynamicImages, setDynamicImages] = useState([]);
  const [dynamicVideos, setDynamicVideos] = useState([]);
  const [dynamicTestimonials, setDynamicTestimonials] = useState(testimonials);
  const [dynamicHeroImages, setDynamicHeroImages] = useState([]);

  useEffect(() => {
    seedLocalDefaultsIfEmpty();
    const localAlbums = getLocalAlbums();
    const deletedAlbumIds = new Set(getDeletedAlbumIds());
    const deletedAlbumTitles = new Set(getDeletedAlbumTitles());
    const deletedVideoIds = new Set(getDeletedVideoIds());
    const deletedVideoTitles = new Set(getDeletedVideoTitles());
    const localImages = getLocalImages();
    const localTestimonials = getLocalTestimonials();
    const localVideos = getLocalVideos();
    const localHomepage = getLocalHomepageSettings();

    if (localAlbums.length > 0) {
      setDynamicAlbums(
        localAlbums.filter(
          (album) => !deletedAlbumIds.has(album.id) && !deletedAlbumTitles.has(String(album.title || '').trim())
        )
      );
    }
    if (localImages.length > 0) setDynamicImages(localImages);
    if (localTestimonials.length > 0) setDynamicTestimonials(localTestimonials);
    if (localVideos.length > 0) {
      setDynamicVideos(
        localVideos.filter(
          (video) => !deletedVideoIds.has(video.id) && !deletedVideoTitles.has(String(video.title || '').trim())
        )
      );
    }
    const validLocalHeroImages = Array.isArray(localHomepage.heroSliderImages)
      ? localHomepage.heroSliderImages.filter(isRenderableImageUrl)
      : [];
    const immediateLocalImageFallback = localImages
      .map((img) => img.url)
      .filter(isRenderableImageUrl)
      .slice(0, 8);

    if (validLocalHeroImages.length > 0) {
      setDynamicHeroImages(validLocalHeroImages);
    } else if (immediateLocalImageFallback.length > 0) {
      setDynamicHeroImages(immediateLocalImageFallback);
    }

    const loadDynamicContent = async () => {
      try {
        const [apiAlbums, apiImages, apiVideos] = await Promise.all([
          getAlbums(),
          getImages(),
          getVideos()
        ]);
        let heroFromSettings = [];

        try {
          const homepageSettingsRes = await getHomepageSettings();
          heroFromSettings = await Promise.all(
            (homepageSettingsRes?.heroSliderImages || []).map((image) => resolveImageUrl(image))
          );
        } catch (settingsError) {
          heroFromSettings = [];
        }

        const normalizedLocalHeroImages = await Promise.all(
          (localHomepage.heroSliderImages || []).map((image) => resolveImageUrl(image))
        );

        const validHomepageHeroImages = heroFromSettings.filter(isRenderableImageUrl);
        const validNormalizedLocalHeroImages = normalizedLocalHeroImages.filter(isRenderableImageUrl);
        const apiImageUrls = apiImages
          .map((img) => img.url)
          .filter(isRenderableImageUrl)
          .slice(0, 8);

        if (apiAlbums.length > 0) {
          setDynamicAlbums(
            apiAlbums.filter(
              (album) => !deletedAlbumIds.has(album.id) && !deletedAlbumTitles.has(String(album.title || '').trim())
            )
          );
        }
        if (apiImages.length > 0) setDynamicImages(apiImages);
        if (apiVideos.length > 0) {
          setDynamicVideos(
            apiVideos.filter(
              (video) => !deletedVideoIds.has(video.id) && !deletedVideoTitles.has(String(video.title || '').trim())
            )
          );
        }

        if (validHomepageHeroImages.length > 0) {
          setDynamicHeroImages(validHomepageHeroImages);
          setLocalHomepageSettings({ heroSliderImages: validHomepageHeroImages });
        } else if (apiImageUrls.length > 0) {
          setDynamicHeroImages(apiImageUrls);
        } else if (validNormalizedLocalHeroImages.length > 0) {
          setDynamicHeroImages(validNormalizedLocalHeroImages);
          setLocalHomepageSettings({ heroSliderImages: validNormalizedLocalHeroImages });
        } else {
          setDynamicHeroImages(immediateLocalImageFallback);
        }
      } catch (error) {
        // Keep local/static fallback data when API is unavailable
        console.error("Failed to load dynamic content:", error);
        if (validLocalHeroImages.length === 0) {
          setDynamicHeroImages(immediateLocalImageFallback);
        }
      }
    };

    loadDynamicContent();
  }, []);

  const handleViewAlbum = (album) => {
    setSelectedAlbum(album);
    setIsAlbumModalOpen(true);
  };

  const handleImageClick = (imageUrl, imagesList) => {
    setLightboxImage(imageUrl);
    setLightboxImages(imagesList);
    setIsLightboxOpen(true);
  };

  const handleBookNow = () => {
    setIsBookingModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoManager />
      {/* Navbar */}
      <Navbar onBookNow={handleBookNow} />
      
      {/* Hero Section with Slider */}
      <Hero onBookNow={handleBookNow} sliderImages={dynamicHeroImages} />
      
      {/* Albums Section - Simple design */}
      <SimpleAlbumsSection albums={dynamicAlbums} onViewAlbum={handleViewAlbum} />
      
      <DeferredSection minHeight="36rem">
        <VideoStoriesSection videos={dynamicVideos} />
      </DeferredSection>
      
      <DeferredSection minHeight="30rem">
        <AboutSection />
      </DeferredSection>
      
      <DeferredSection minHeight="28rem">
        <TestimonialsSection testimonials={dynamicTestimonials} />
      </DeferredSection>
      
      <DeferredSection minHeight="42rem">
        <RealMosaicGallery images={dynamicImages} onImageClick={handleImageClick} />
      </DeferredSection>

      <DeferredSection minHeight="28rem">
        <BlogSection />
      </DeferredSection>
      
      <DeferredSection minHeight="28rem">
        <ContactSection onBookNow={handleBookNow} />
      </DeferredSection>
      
      {/* Footer */}
      <Footer />
      
      {/* Modals */}
      <AlbumModal
        album={selectedAlbum}
        isOpen={isAlbumModalOpen}
        onClose={() => setIsAlbumModalOpen(false)}
        onImageClick={handleImageClick}
      />
      <LightboxModal
        image={lightboxImage}
        images={lightboxImages}
        isOpen={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
      />
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
      />
      <WebsiteAssistant albums={dynamicAlbums} videos={dynamicVideos} />
      
      <Toaster />
    </div>
  );
};

const SeoManager = () => {
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    const origin = SITE_URL || window.location.origin;
    const canonicalUrl = `${origin}${location.pathname}`;
    const blogPost = location.pathname.startsWith('/blog/') ? getBlogPostBySlug(params.slug) : null;
    const title = blogPost?.seoTitle || "Sagar Sen Photography - Professional Wedding Photographer";
    const description =
      blogPost?.description ||
      "Wedding photography, bridal portraits, cinematic wedding films, and storytelling albums by Sagar Sen Photography.";
    const imageUrl = `${origin}/og-image.svg`;

    document.title = title;

    const ensureMeta = (selector, attribute, value) => {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, selector.includes("property=") ? selector.match(/property="([^"]+)"/)?.[1] || "" : selector.match(/name="([^"]+)"/)?.[1] || "");
        document.head.appendChild(element);
      }
      element.setAttribute("content", value);
    };

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    ensureMeta('meta[name="description"]', "name", description);
    ensureMeta('meta[property="og:title"]', "property", title);
    ensureMeta('meta[property="og:description"]', "property", description);
    ensureMeta('meta[property="og:url"]', "property", canonicalUrl);
    ensureMeta('meta[property="og:image"]', "property", imageUrl);
    ensureMeta('meta[property="twitter:title"]', "property", title);
    ensureMeta('meta[property="twitter:description"]', "property", description);
    ensureMeta('meta[property="twitter:image"]', "property", imageUrl);

    let schema = document.getElementById("site-jsonld");
    if (!schema) {
      schema = document.createElement("script");
      schema.id = "site-jsonld";
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }

    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      ...(blogPost
        ? {
            "@type": "BlogPosting",
            headline: blogPost.title,
            description,
            keywords: blogPost.keywords.join(', '),
            datePublished: blogPost.publishedAt,
            dateModified: blogPost.publishedAt,
            mainEntityOfPage: canonicalUrl,
            image: imageUrl,
            author: {
              "@type": "Person",
              name: "Sagar Sen"
            },
            publisher: {
              "@type": "Organization",
              name: "Sagar Sen Photography"
            }
          }
        : {
            "@type": "ProfessionalService",
            name: "Sagar Sen Photography",
            image: imageUrl,
            url: canonicalUrl,
            description,
            areaServed: "India",
            serviceType: "Wedding Photography",
            sameAs: []
          })
    });
  }, [location.pathname, params.slug]);

  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/blog/:slug" element={<><SeoManager /><BlogPostPage /></>} />
          <Route path="/admin" element={<ProtectedAdmin />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

const AdminLoginPage = ({ onSubmit, value, onChange, error }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Admin Login</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <Input
            type="password"
            placeholder="Enter admin password"
            value={value}
            onChange={onChange}
            autoComplete="current-password"
            required
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" className="w-full">
            Login
          </Button>
        </form>
      </CardContent>
    </Card>
  </div>
);

const ProtectedAdmin = () => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminPasswordInput, setAdminPasswordInput] = useState("");
  const [adminError, setAdminError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [bootstrappingAuth, setBootstrappingAuth] = useState(true);

  const ensureFirebaseWriteSession = async (password) => {
    if (auth.currentUser) return;

    if (ADMIN_EMAIL) {
      await signInWithEmailAndPassword(auth, ADMIN_EMAIL, password);
      return;
    }

    await signInAnonymously(auth);
  };

  useEffect(() => {
    const authenticated = sessionStorage.getItem(ADMIN_SESSION_KEY) === "true";
    if (!authenticated) {
      setBootstrappingAuth(false);
      return;
    }

    ensureFirebaseWriteSession(ADMIN_PASSWORD)
      .then(() => {
        setIsAdminAuthenticated(true);
        setAdminError("");
      })
      .catch((error) => {
        console.error("Failed to restore Firebase admin session:", error);
        sessionStorage.removeItem(ADMIN_SESSION_KEY);
        setIsAdminAuthenticated(false);
        setAdminError(getFirebaseAuthErrorMessage(error));
      })
      .finally(() => {
        setBootstrappingAuth(false);
      });
  }, []);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    const expectedPassword = String(ADMIN_PASSWORD).trim();
    const enteredPassword = adminPasswordInput.trim();

    if (enteredPassword !== expectedPassword) {
      setAdminError("Invalid admin login");
      return;
    }

    try {
      setAuthLoading(true);
      await ensureFirebaseWriteSession(enteredPassword);
      sessionStorage.setItem(ADMIN_SESSION_KEY, "true");
      setIsAdminAuthenticated(true);
      setAdminError("");
      setAdminPasswordInput("");
    } catch (error) {
      console.error("Firebase admin auth failed:", error);
      sessionStorage.removeItem(ADMIN_SESSION_KEY);
      setIsAdminAuthenticated(false);
      setAdminError(getFirebaseAuthErrorMessage(error));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAdminLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Firebase sign-out failed:", error);
    }
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    setIsAdminAuthenticated(false);
    setAdminPasswordInput("");
    setAdminError("");
  };

  if (bootstrappingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-500">
        Checking admin session...
      </div>
    );
  }

  if (!isAdminAuthenticated) {
    return (
      <AdminLoginPage
        onSubmit={handleAdminLogin}
        value={adminPasswordInput}
        onChange={(e) => setAdminPasswordInput(e.target.value)}
        error={authLoading ? "Signing in..." : adminError}
      />
    );
  }

  return (
    <div>
      <div className="sticky top-0 z-50 bg-white border-b px-4 py-2 flex justify-end">
        <Button variant="outline" size="sm" onClick={handleAdminLogout}>
          Logout
        </Button>
      </div>
      <Suspense
        fallback={
          <div className="flex min-h-[40vh] items-center justify-center text-sm text-gray-500">
            Loading admin panel...
          </div>
        }
      >
        <AdminPanelLazy />
      </Suspense>
    </div>
  );
};

export default App;
